/**
 * Clipboard Operation Handlers
 * Extracted from projectDataUtils.ts applyOperation
 * Handles copy, cut, paste, duplicate operations
 */

import type { ProjectData, NodeOperation } from '@/shared/types/projectData';
import { cloneProjectData, createNode, moveNode } from '@/shared/utils/projectDataUtils';
import { deepClone } from '@/shared/utils/clone';
import { resolveCutNodes } from '../clipboardOperations';

const findNodeById = (data: ProjectData, nodeId: string) =>
  data.nodes.find(n => n.id === nodeId) || null;

export function handleCopyNodes(data: ProjectData, _op: NodeOperation): ProjectData {
  // Copy operation doesn't modify data, just stores in clipboard
  return data;
}

export function handleCutNodes(data: ProjectData, _op: NodeOperation): ProjectData {
  // Cut operation marks nodes but doesn't delete them yet
  // Actual deletion happens on paste
  return data;
}

export function handlePasteNodes(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.clipboardData || !op.pasteParentId) return data;
  const wasCut = Boolean(op.cutNodeIds?.length);
  const nodes = op.clipboardData;
  if (wasCut) {
    const sourceIds = new Set(op.sourceNodeIds || op.cutNodeIds);
    const current = resolveCutNodes(
      data,
      nodes.filter(node => sourceIds.has(node.id)),
      op.pasteParentId,
      typeof op.metadata?.projectId === 'string' ? op.metadata.projectId : data.projectId
    );
    if (!current?.length) return data;
    // Cut is a move: preserving identity keeps attachments, dependencies and
    // descendants attached, including children created after preparation.
    const movingIds=new Set(current.map(node=>node.id));
    const roots=current.filter(node=>!node.parentId||!movingIds.has(node.parentId));
    let moved=data;
    let targetIndex=op.pasteIndex||0;
    for(const root of roots)moved=moveNode(moved,root.id,op.pasteParentId,targetIndex++);
    return moved;
  }
  const pastedNodes = deepClone(nodes);
  let updated = cloneProjectData(data);

  // Add pasted nodes with new IDs
  let rootIndex = op.pasteIndex || 0;
  const pastedIds = new Set(pastedNodes.map(node => node.id));
  pastedNodes.forEach((node) => {
    const newNode = { ...node };
    // Apply ID mappings if provided
    if (op.duplicatedNodeMappings) {
      const mapping = op.duplicatedNodeMappings.find(m => m.originalId === node.id);
      if (mapping) {
        newNode.id = mapping.newId;
      }
    }
    const isDescendant = node.parentId && pastedIds.has(node.parentId);
    const parentId = isDescendant
      ? op.duplicatedNodeMappings?.find(mapping => mapping.originalId === node.parentId)?.newId
      : op.pasteParentId;
    delete newNode.children;
    if(Array.isArray(newNode.dependencies))newNode.dependencies=newNode.dependencies.map(id=>op.duplicatedNodeMappings?.find(mapping=>mapping.originalId===id)?.newId||id);
    updated = createNode(updated, newNode, parentId,
      isDescendant ? node.order : rootIndex++);
  });

  return updated;
}

export function handleDuplicateNode(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.nodeId || !op.data) return data;
  const originalNode = findNodeById(data, op.nodeId);
  if (!originalNode) return data;

  const duplicatedNode = { ...op.data };
  // Apply new ID
  if (op.duplicatedNodeMappings && op.duplicatedNodeMappings[0]) {
    duplicatedNode.id = op.duplicatedNodeMappings[0].newId;
  }

  return createNode(data, duplicatedNode, op.parentId, op.targetIndex);
}

export function handleDuplicateSubtree(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.nodeId || !op.clipboardData) return data;
  let updated = cloneProjectData(data);

  // Add all nodes in the subtree with new IDs
  op.clipboardData.forEach((node, index) => {
    const newNode = { ...node };
    // Apply ID mappings
    if (op.duplicatedNodeMappings) {
      const mapping = op.duplicatedNodeMappings.find(m => m.originalId === node.id);
      if (mapping) {
        newNode.id = mapping.newId;
        // Update parent references
        if (newNode.parentId) {
          const parentMapping = op.duplicatedNodeMappings.find(
            m => m.originalId === newNode.parentId
          );
          if (parentMapping) {
            newNode.parentId = parentMapping.newId;
          }
        }
      }
    }
    updated = createNode(
      updated,
      newNode,
      index === 0 ? op.parentId : newNode.parentId,
      op.targetIndex
    );
  });

  return updated;
}
